import React, { useCallback, useMemo, useState } from "react";

import {
  findLast,
  findLastIndex,
  first,
  groupBy,
  last,
  map,
  meanBy,
  sortBy,
  sumBy,
} from "lodash";

import {
  Annotation,
  Axis,
  Chart,
  Legend,
  LineAdvance,
  Tooltip,
} from "bizcharts";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  VStack,
} from "@chakra-ui/react";

import useDateFnsLocale from "../../hooks/useDateFnsLocale";
import { dateFormatter } from "../../hooks/useFormatDate";
import useTranslations from "../../hooks/useTranslations";

import {
  Card,
  CardBody,
  CardHeader,
  Button as WombatButton,
} from "../wombat-ui";

import { getClosestGrade } from "../../utils/reviewUtils";
import TinyUserGradeOverTimeLineChart from "../analytics/charts/TinyUserGradeOverTimeLineChart";
import { QuestionSummaryFragment, ReviewFullFragment, ReviewReportFullFragment } from '../../graphql/generated/definitions';

export type Datum = {
  data: {
    user: string;
    grade: number;
    date: number;
    isCollaborator: boolean;
    isReferentAverage: boolean;
    timeSpent?: number;
  }[];
  metadata: {
    title: string;
    question: QuestionSummaryFragment;
    reportId?: string;
    objective?: {
      label: string;
      value: string;
    };
    globalTimeSpent?: number;
  };
};

type ChartData = {
  QuestionEvaluation: { [digest: string]: Datum };
  QuestionSkill: { [digest: string]: Datum };
};

function getDataFromReports(
  reports: ReviewReportFullFragment[],
  averageLabel: string
) {
  const chartData: ChartData = {
    QuestionEvaluation: {},
    QuestionSkill: {},
  };

  reports.forEach(({ answers, checkedAt }) => {
    if (!checkedAt) return;
    const checkedAtTimestamp = new Date(checkedAt).getTime();

    answers.forEach(
      ({
        type,
        normalizedRating,
        user,
        timeSpent,
        isCollaborator,
        question,
        reportId,
      }) => {
        // Process Skills related questions
        if (type === "AnswerSkill") {
          // If the question haven't yet been processed
          if (!chartData.QuestionSkill[question.digest])
            chartData.QuestionSkill[question.digest] = {
              data: [
                {
                  user: user.name,
                  grade: normalizedRating,
                  date: checkedAtTimestamp,
                  timeSpent,
                  isCollaborator,
                  isReferentAverage: false,
                },
              ],
              metadata: {
                question,
                title: question.label,
                reportId,
                objective: question.options.find((o) => o.isGoal),
              },
            };
          // Otherwise append the new retrieve data
          else
            chartData.QuestionSkill[question.digest].data.push({
              user: user.name,
              grade: normalizedRating,
              date: checkedAtTimestamp,
              timeSpent,
              isCollaborator,
              isReferentAverage: false,
            });
        }

        // Process Evaluations related questions
        if (type === "AnswerEvaluation") {
          // If the question haven't yet been processed
          if (!chartData.QuestionEvaluation[question.digest])
            chartData.QuestionEvaluation[question.digest] = {
              data: [
                {
                  user: user.name,
                  grade: normalizedRating,
                  date: checkedAtTimestamp,
                  timeSpent,
                  isCollaborator,
                  isReferentAverage: false,
                },
              ],
              metadata: {
                question,
                title: question.label,
                reportId,
                objective: question.options.find((o) => o.isGoal),
              },
            };
          // Otherwise append the new retrieve data
          else
            chartData.QuestionEvaluation[question.digest].data.push({
              user: user.name,
              grade: normalizedRating,
              date: checkedAtTimestamp,
              timeSpent,
              isCollaborator,
              isReferentAverage: false,
            });
        }
      }
    );
  });

  // Compute every entries referentAverage grade & timeSpent,
  // Append the global timeSpent to the datum metadata
  Object.entries(chartData).forEach(([, chartDataEntry]) =>
    Object.entries(chartDataEntry).forEach(([, datum]) => {
      const groupedData = groupBy(datum.data, "date");

      Object.entries(groupedData).forEach(([, groupedEntry]) => {
        const referentEntries = groupedEntry.filter((entry) => !entry.isCollaborator);
        if (
          referentEntries.length === 0 ||
          referentEntries.every((entry) => entry.grade === null)
        )
          return;

        return datum.data.push({
          user: averageLabel,
          grade: meanBy(referentEntries, "grade"),
          timeSpent: meanBy(referentEntries, "timeSpent"),
          date: first(groupedEntry).date,
          isCollaborator: false,
          isReferentAverage: true,
        });
      });

      const averagedDataGroup = groupBy(datum.data, "user")[averageLabel];

      datum.metadata.globalTimeSpent = sumBy(averagedDataGroup, "timeSpent");
    })
  );

  // Ordering every entries by date
  Object.entries(chartData).forEach(([, chartDataEntry]) =>
    Object.entries(chartDataEntry).forEach(([key, datum]) => {
      chartDataEntry[key].data = sortBy(datum.data, [
        "date",
        "isCollaborator",
        "isReferentAverage",
      ]);
    })
  );

  return chartData;
}

/** Retrieves the latest data for each user involved in the grade */
function getLastDatumEntry(datum: Datum, review: ReviewFullFragment) {
  const referentNames = map(review.referents, "name");

  return [
    datum.data[
      findLastIndex(datum.data, (item) => !referentNames.includes(item.user))
    ],
    ...referentNames.map(
      (referentName) =>
        datum.data[
          findLastIndex(datum.data, (item) => referentName === item.user)
        ]
    ),
  ];
}

type UserFollowUpAnalyticsDetailModalProps = {
  datum?: Datum;
  open: boolean;
  options: {
    value: number;
    label: string;
  }[];
  onClose: () => void;
};

const UserFollowUpAnalyticsDetailModal: React.FC<UserFollowUpAnalyticsDetailModalProps> =
  ({ datum, options, open, onClose }) => {
    const t = useTranslations();
    const dateFnsLocale = useDateFnsLocale();

    const formatter = useCallback(
      (v: string | number | Date) =>
        dateFormatter(
          // Bizcharts internally converts Date to timestamp string
          typeof v === "string" ? parseInt(v) : v,
          t(`dateFormat.short`) as string,
          t("dateFormat.fallback") as string,
          dateFnsLocale
        ),
      [dateFnsLocale]
    );

    const lastReferentGrade = useMemo(
      () =>
        findLast(datum?.data, ({ isReferentAverage }) => isReferentAverage)
          ?.grade,
      [datum]
    );
    const lastCollaboratorGrade = useMemo(
      () =>
        findLast(datum?.data, ({ isReferentAverage }) => !isReferentAverage)
          ?.grade,
      [datum]
    );

    const objective = useMemo(() => {
      if (datum?.metadata?.objective)
        return options.length > 1
          ? (1 / (options.length - 1)) *
              parseInt(datum.metadata.objective.value)
          : 1;
    }, [datum?.metadata?.objective, options]);

    if (!open || !datum) return null;

    return (
      <Modal isOpen={open} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{datum.metadata.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={8}>
              <StatGroup>
                <Stat>
                  <StatLabel>{t("reports.timeSpentInHour")}</StatLabel>
                  <StatNumber>{datum.metadata.globalTimeSpent}</StatNumber>
                  {datum.metadata.question.expectedTimeValue && (
                    <StatHelpText>
                      {t("reviews.expectedTimeWithValue", {
                        count: datum.metadata.question.expectedTimeValue,
                      })}
                    </StatHelpText>
                  )}
                </Stat>
                <Stat>
                  <StatLabel>{t("reviews.currentLevel")}</StatLabel>
                  <StatNumber>
                    {
                      getClosestGrade(
                        options,
                        lastReferentGrade || lastCollaboratorGrade
                      ).label
                    }
                  </StatNumber>
                  {objective && (
                    <StatHelpText>
                      {t("reviews.targetLevel", {
                        level: getClosestGrade(options, objective).label,
                      })}
                    </StatHelpText>
                  )}
                </Stat>
              </StatGroup>
              <Chart
                autoFit
                height={400}
                data={datum.data}
                padding="auto"
                scale={{
                  grade: {
                    range: [0, 1],
                    ticks: options.map((option) => option.value),
                  },
                }}
              >
                <LineAdvance
                  position="date*grade"
                  color="user"
                  shape="smooth"
                  tooltip={[
                    "user*grade",
                    (name, v) => {
                      return {
                        value: getClosestGrade(options, v).label,
                        name,
                      };
                    },
                  ]}
                  point
                />
                {datum?.metadata?.objective && (
                  <Annotation.Line
                    start={["min", objective]}
                    end={["max", objective]}
                    text={{
                      position: "end",
                      content: t("reviews.targetLevel", {
                        level: getClosestGrade(options, objective).label,
                      }) as string,
                      offsetX: -40,
                      offsetY: -5,
                      style: {
                        fill: "#ff0000",
                      },
                    }}
                    style={{
                      lineDash: [2, 4],
                      stroke: "#ff0000",
                    }}
                  />
                )}
                <Axis
                  name="date"
                  label={{
                    formatter,
                    autoRotate: true,
                  }}
                />
                <Axis
                  name="grade"
                  position="left"
                  label={{
                    formatter: (v) =>
                      getClosestGrade(options, parseFloat(v)).label,
                    autoEllipsis: true,
                  }}
                />
                <Legend
                  name="user"
                  position="bottom"
                  offsetY={20}
                  filter={(v) =>
                    v !==
                    (t(
                      "statistics.subSections.referentSkillRatingAverage"
                    ) as string)
                  }
                />
                <Tooltip
                  shared
                  showMarkers
                  showCrosshairs
                  title={(
                    _,
                    datum: {
                      date: Date;
                    }
                  ) => formatter(datum.date)}
                />
              </Chart>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} aria-label="Close modal">
              {t("forms.actions.close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

type UserFollowUpAnalyticsProps = {
  review: ReviewFullFragment;
  options: {
    value: number;
    label: string;
  }[];
  type: "QuestionSkill" | "QuestionEvaluation";
  emptyAnalyticsLabel: string,
};

const UserFollowUpAnalytics: React.FC<UserFollowUpAnalyticsProps> = ({
  review,
  options,
  type,
  emptyAnalyticsLabel
}) => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  const [detailsModalState, setDetailsModalState] = useState<
    Pick<UserFollowUpAnalyticsDetailModalProps, "open" | "datum">
  >({
    open: false,
  });

  const formatter = useCallback(
    (v: string | number | Date) =>
      dateFormatter(
        // Bizcharts internally converts Date to timestamp string
        typeof v === "string" ? parseInt(v) : v,
        t(`dateFormat.short`) as string,
        t("dateFormat.fallback") as string,
        dateFnsLocale
      ),
    [dateFnsLocale]
  );

  const evolutionChartData = useMemo(
    () =>
      getDataFromReports(
        review.reports || [],
        t("statistics.subSections.referentSkillRatingAverage") as string
      ),
    [review]
  );

  const isEmpty = useMemo(
    () => Object.keys(evolutionChartData[type]).length === 0,
    [evolutionChartData, type]
  );

  return isEmpty ? (
    <div className="flex justify-center text-sm text-light-900">
      {emptyAnalyticsLabel}
    </div>
  ) : (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
      {Object.entries(evolutionChartData[type])?.map(([key, datum]) => {
        const lastDatumEntry = getLastDatumEntry(datum, review);
        const hasEvolution = datum.data.length > lastDatumEntry.length;
        const referentsLastGrades = lastDatumEntry.filter(
          (entry) => !entry?.isCollaborator
        );

        const noReferentAnswers =
          referentsLastGrades.length > 0 &&
          referentsLastGrades.every((datum) => !datum?.grade);

        return (
          <Card key={key} className="overflow-auto">
            <CardHeader
              description={t("templates.lastUpdatedAt", {
                date: formatter(last(datum.data).date),
              })}
            >
              {datum.metadata.title}
            </CardHeader>
            <CardBody className="flex-col">
              {hasEvolution && (
                <TinyUserGradeOverTimeLineChart
                  datum={datum}
                  options={options}
                  scale={{
                    grade: {
                      range: [0, 1],
                      ticks: options.map((option) => option.value),
                    },
                  }}
                  filter={[
                    [
                      "grade",
                      (_, datum) => {
                        return datum.isCollaborator || datum.isReferentAverage;
                      },
                    ],
                  ]}
                />
              )}
              {!hasEvolution && (
                <div className="flex justify-center text-sm h-52 text-light-900">
                  {t("reviews.noEvolutionTrackedYet")}
                </div>
              )}
              <div className="grid grid-cols-2 pt-4 divide-x">
                <div className="flex flex-col items-center justify-center">
                  <p className="text-sm text-light-900">
                    {t("reviews.referents")}
                  </p>

                  {referentsLastGrades?.length > 0 && !noReferentAnswers ? (
                    <p className="text-lg font-bold text-center">
                      {
                        getClosestGrade(
                          options,
                          meanBy(referentsLastGrades, "grade")
                        ).label
                      }
                    </p>
                  ) : (
                    <p className="text-xs text-center text-light-900">
                      {noReferentAnswers
                        ? t("reviews.noReferentGrading")
                        : t("reviews.noReferent")}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-center justify-center">
                  <p className="text-sm text-light-900">
                    {t("reviews.collaborator")}
                  </p>
                  <p className="text-lg font-bold text-center">
                    {
                      getClosestGrade(
                        options,
                        lastDatumEntry.find((entry) => entry?.isCollaborator)?.grade
                      ).label
                    }
                  </p>
                </div>
              </div>
              <WombatButton
                className="pt-4"
                variant="link"
                scale="xs"
                block
                id={`${datum.metadata.title}-seeMore`}
                onClick={() => setDetailsModalState({ open: true, datum })}
              >
                {t("reviews.card.seeMore")}
              </WombatButton>
            </CardBody>
          </Card>
        );
      })}
      <UserFollowUpAnalyticsDetailModal
        {...detailsModalState}
        options={options}
        onClose={() =>
          setDetailsModalState((prev) => ({ ...prev, open: false }))
        }
      />
    </div>
  );
};

export default React.memo(UserFollowUpAnalytics);
